import { useEffect, useMemo, useState } from 'react';
import qrcode from 'qrcode-generator';
import { encodeProvisioningUri, type Extension, type MobileProvisioning } from '@arcnet/contracts';
import { api } from '../../lib/api/client.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { useSession } from '../../lib/auth/session.js';
import { Icon } from '../../components/ui/Icon.js';
import { toastError } from '../../components/ui/Toast.js';

/**
 * Setting this extension up on the ArcSIP app, by QR code.
 *
 * Lives in the provisioning section with the rest of it, and is rendered as a
 * section of the extension's Phone setup rather than owning one: somebody
 * setting up a new starter does the desk phone and the mobile in one sitting.
 *
 * The app scans the code and fills in the account -- extension, password,
 * server, port, transport and codecs. The link format is shared with the app
 * (contracts provisioning.ts) and versioned, so settings can be added without
 * breaking an app that predates them.
 *
 * The code carries the SIP password, so it is treated exactly like revealing
 * one: the same step-up check, its own audit entry, kept only in this
 * component's state, and cleared after two minutes -- a QR code left open on a
 * desk is a password on a poster.
 */

const SHOW_FOR_SEC = 120;

interface Reply { recoverable: boolean; reason?: string; provisioning?: MobileProvisioning }

export function MobileProvisioning({ tenantId, ext }: { tenantId: string; ext: Extension }) {
  const can = useSession((s) => s.can);
  const step = useStepUp();
  const [p, setP] = useState<MobileProvisioning | null>(null);
  const [reason, setReason] = useState<string | null>(null);
  const [left, setLeft] = useState(0);
  const [busy, setBusy] = useState(false);
  const mayReveal = can('extension:credentials:reveal');

  const hide = () => { setP(null); setLeft(0); };

  useEffect(() => {
    if (!p) return;
    setLeft(SHOW_FOR_SEC);
    const t = setInterval(() => setLeft((s) => {
      if (s <= 1) { clearInterval(t); setP(null); return 0; }
      return s - 1;
    }), 1000);
    return () => clearInterval(t);
  }, [p]);

  const show = async () => {
    setBusy(true); setReason(null);
    try {
      // The action applies its own result: the step-up hook replays a held
      // action after the password is confirmed, but does not hand back what it
      // returned -- so returning alone would need a second click.
      await step.run(async () => {
        const r = await api.get<Reply>(`/v1/tenants/${tenantId}/extensions/${ext.id}/mobile-provisioning`);
        if (r.recoverable && r.provisioning) setP(r.provisioning);
        else setReason(r.reason ?? 'This extension’s password cannot be shown.');
        return r;
      });
    } catch (e) {
      toastError(e);
    } finally {
      setBusy(false);
    }
  };

  const uri = useMemo(() => (p ? encodeProvisioningUri(p) : null), [p]);

  return (
    <>
      <div className="arc-card" style={{ marginBottom: 14 }}>
        <div className="arc-card-head">
          <h2 className="arc-h2">ArcSIP app</h2>
          <span className="arc-subtle">Scan to set up this extension</span>
        </div>
        <div className="arc-card-body">
          <StepUpPrompt step={step} what="Showing a setup code" />
          {reason && (
            <div className="arc-banner arc-banner-warn" style={{ marginBottom: 12 }}>
              <Icon name="warning" size={14} />
              <span>{reason}</span>
            </div>
          )}

          {p && uri ? (
            <div className="arc-row" style={{ gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <QrCode text={uri} />
              <div className="arc-stack" style={{ gap: 8, minWidth: 200, flex: 1 }}>
                <span className="arc-row" style={{ gap: 6, fontSize: 12.5 }}>
                  <Icon name="warning" size={13} style={{ color: 'var(--arc-warn)' }} />
                  Contains the SIP password for {p.user}.
                </span>
                <span className="arc-subtle arc-num" style={{ fontSize: 12 }}>
                  Hides in {Math.floor(left / 60)}:{String(left % 60).padStart(2, '0')}
                </span>
                <div>
                  <button type="button" className="arc-btn arc-btn-sm" onClick={hide}>Hide now</button>
                </div>
              </div>
            </div>
          ) : (
            <div className="arc-row" style={{ gap: 12, flexWrap: 'wrap' }}>
              <p className="arc-muted" style={{ margin: 0, fontSize: 12.5, flex: '1 1 260px' }}>
                {mayReveal
                  ? 'Show a code and scan it in the app. It carries the password, so it hides after two minutes.'
                  : 'Needs permission to reveal SIP passwords.'}
              </p>
              <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
                disabled={busy || !mayReveal} onClick={() => void show()}>
                {busy ? 'Loading…' : 'Show setup code'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* No manual-setup card here any more: the same username, domain and
          password are Phone setup's first card, for any phone (15 September 2026). */}
    </>
  );
}

/**
 * The code itself, as plain SVG squares. Always black on white whatever the
 * theme -- scanners expect dark on light, and a themed code fails in dark mode.
 * Drawn as React elements, so nothing is injected as markup. Error correction
 * M: survives a glossy screen without making the code dense.
 */
function QrCode({ text }: { text: string }) {
  const { size, path } = useMemo(() => {
    const qr = qrcode(0, 'M');
    qr.addData(text, 'Byte');
    qr.make();
    const n = qr.getModuleCount();
    let d = '';
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) if (qr.isDark(r, c)) d += `M${c},${r}h1v1h-1z`;
    }
    return { size: n, path: d };
  }, [text]);
  const quiet = 4;   // the blank border scanners need
  const box = size + quiet * 2;
  return (
    <svg viewBox={`${-quiet} ${-quiet} ${box} ${box}`} width={216} height={216}
      role="img" aria-label="Setup code for the ArcSIP app" shapeRendering="crispEdges"
      style={{ borderRadius: 'var(--arc-r-lg)', border: '1px solid var(--arc-border)', flex: '0 0 216px' }}>
      <rect x={-quiet} y={-quiet} width={box} height={box} fill="#ffffff" />
      <path d={path} fill="#000000" />
    </svg>
  );
}
